import { html, nothing, type TemplateResult } from "lit-html";
import type { View, App } from "../app.js";
import { api } from "../api.js";
import type { Dimension, Metric, QueryResult, RangePreset, WidgetQuery, WidgetSpec } from "../types.js";
import { resultTable, downloadCsv, sectionHead } from "../components.js";
import { formatMetric } from "../format.js";
import { hbars } from "../charts.js";
import { icons } from "../icons.js";

export const DIMS: [Dimension, string][] = [
  ["month", "Month"], ["quarter", "Quarter"], ["year", "Year"], ["division", "Division"], ["subdivision", "Subdivision"],
  ["manager", "Manager"], ["rep", "Rep"], ["customer", "Customer"], ["state", "State"], ["county", "County"], ["account", "Income account"],
];
export const METRICS: [Metric, string][] = [["revenue", "Revenue"], ["gp", "Gross profit"], ["gp_pct", "GP %"], ["invoices", "Invoices"], ["customers", "Customers"]];
export const RANGES: [RangePreset, string][] = [["ytd", "Year to date"], ["last_12_months", "Last 12 months"], ["this_month", "This month"], ["last_month", "Last month"], ["last_year", "Last year"], ["all", "All time"], ["custom", "Custom"]];

interface State {
  dim1: Dimension;
  dim2: Dimension | "";
  metrics: Metric[];
  range: RangePreset;
  from: string;
  to: string;
  compare: boolean;
  subdivision: string;
  limit: number;
}
const st: State = { dim1: "month", dim2: "", metrics: ["revenue"], range: "ytd", from: "", to: "", compare: true, subdivision: "", limit: 100 };
let pinTitle = "";
let pinMsg: { ok: boolean; text: string } | null = null;

const TIME = new Set<Dimension>(["month", "quarter", "year"]);

function buildQuery(app: App): WidgetQuery {
  const dims = [st.dim1, ...(st.dim2 && st.dim2 !== st.dim1 ? [st.dim2] : [])] as Dimension[];
  const compare = st.compare && !dims.includes("year") && st.range !== "all";
  return {
    dims,
    metrics: st.metrics.length ? st.metrics : ["revenue"],
    filters: { ...app.queryFilters(), ...(st.subdivision ? { subdivision: [st.subdivision] } : {}) },
    range: st.range,
    ...(st.range === "custom" ? { from: st.from, to: st.to } : {}),
    ...(compare ? { compare: "prior_year" as const } : {}),
    limit: st.limit,
  };
}

/** Chart for a single-dimension result (used here and on My Board). */
export function resultChart(r: QueryResult, kind: "bar" | "line" | undefined, height = 260): TemplateResult {
  const d = r.dims[0];
  const m = r.metrics[0];
  if (!d || !m) return html``;
  const fmt = (n: number) => formatMetric(n, m.format, true);
  if (TIME.has(d.key)) {
    const rows = [...r.rows].sort((a, b) => String(a.key[d.key]).localeCompare(String(b.key[d.key])));
    const labels = rows.map((x) => x.label[d.key] ?? "");
    if (kind === "line") {
      return html`<ff-line .data=${{ labels, format: fmt, height, series: [
        { name: m.label, values: rows.map((x) => x.values[m.key] ?? null), color: "var(--series-1)", area: true },
        ...(rows[0]?.prior ? [{ name: `${m.label} (prior yr)`, values: rows.map((x) => x.prior?.[m.key] ?? null), color: "var(--series-muted)", muted: true }] : []),
      ] }}></ff-line>`;
    }
    return html`<ff-columns .data=${{ labels, name: m.label, priorName: "Prior year", format: fmt, height,
      values: rows.map((x) => x.values[m.key] ?? null), prior: rows[0]?.prior ? rows.map((x) => x.prior?.[m.key] ?? null) : undefined }}></ff-columns>
      ${rows[0]?.prior ? html`<div class="legend mt"><span><i class="sq" style="background:var(--series-1)"></i>Selected period</span><span><i class="sq" style="background:var(--series-muted);opacity:.6"></i>Same period, prior year</span></div>` : nothing}`;
  }
  return hbars(r.rows.slice(0, 15).map((x) => ({ label: x.label[d.key] ?? "", value: x.values[m.key] ?? 0, ghost: x.prior?.[m.key] ?? null })), fmt, { ghostName: "Prior year" });
}

interface Data { result: QueryResult; query: WidgetQuery }

export const explore: View<Data> = {
  title: () => "Explore",
  filters: ["manager", "rep", "division"],
  async load(app) {
    const query = buildQuery(app);
    if (query.range === "custom" && (!query.from || !query.to)) {
      return { query, result: { dims: [], metrics: [], rows: [], totals: {}, truncated: false, range: { from: null, to: null }, rangeLabel: "Pick dates" } };
    }
    return { query, result: await api.query(query) };
  },
  render(app: App, d) {
    const set = <K extends keyof State>(k: K, v: State[K]) => { st[k] = v; pinMsg = null; app.reload(); };
    const subs = app.me.divisions.flatMap((dv) => dv.subs.map((s) => ({ key: `${dv.key}/${s.key}`, name: `${dv.name} › ${s.name}` })));
    const chartKind: "bar" | "line" = TIME.has(st.dim1) && st.dim1 === "month" ? "line" : "bar";
    const pin = async () => {
      const title = pinTitle.trim() || autoTitle(d.query);
      const spec: WidgetSpec = { kind: d.query.dims.length === 1 ? "chart" : "table", chart: chartKind, query: { ...d.query, limit: Math.min(d.query.limit, 50) } };
      try {
        await api.addWidget(title, spec);
        pinMsg = { ok: true, text: `Pinned “${title}” to My Board.` };
        pinTitle = "";
      } catch (e) {
        pinMsg = { ok: false, text: (e as Error).message };
      }
      app.render();
    };
    return html`
      <section class="glass card">
        ${sectionHead("Build a view", "Manager, rep and division filters at the top apply here too")}
        <div class="builder">
          <div class="field"><label>Group by</label><select class="select" @change=${(e: Event) => set("dim1", (e.target as HTMLSelectElement).value as Dimension)}>
            ${DIMS.map(([k, l]) => html`<option value=${k} ?selected=${st.dim1 === k}>${l}</option>`)}</select></div>
          <div class="field"><label>Then by</label><select class="select" @change=${(e: Event) => set("dim2", (e.target as HTMLSelectElement).value as Dimension | "")}>
            <option value="" ?selected=${!st.dim2}>Nothing</option>
            ${DIMS.filter(([k]) => k !== st.dim1).map(([k, l]) => html`<option value=${k} ?selected=${st.dim2 === k}>${l}</option>`)}</select></div>
          <div class="field"><label>Dates</label><select class="select" @change=${(e: Event) => set("range", (e.target as HTMLSelectElement).value as RangePreset)}>
            ${RANGES.map(([k, l]) => html`<option value=${k} ?selected=${st.range === k}>${l}</option>`)}</select></div>
          ${st.range === "custom" ? html`
            <div class="field"><label>From</label><input class="input" style="height:38px;border-radius:12px" type="date" .value=${st.from} @change=${(e: Event) => set("from", (e.target as HTMLInputElement).value)}></div>
            <div class="field"><label>To</label><input class="input" style="height:38px;border-radius:12px" type="date" .value=${st.to} @change=${(e: Event) => set("to", (e.target as HTMLInputElement).value)}></div>` : nothing}
          <div class="field"><label>Subdivision</label><select class="select" @change=${(e: Event) => set("subdivision", (e.target as HTMLSelectElement).value)}>
            <option value="" ?selected=${!st.subdivision}>All</option>
            ${subs.map((s) => html`<option value=${s.key} ?selected=${st.subdivision === s.key}>${s.name}</option>`)}</select></div>
          <div class="field"><label>Rows</label><select class="select" @change=${(e: Event) => set("limit", Number((e.target as HTMLSelectElement).value))}>
            ${[25, 100, 500, 2000].map((n) => html`<option value=${n} ?selected=${st.limit === n}>Top ${n}</option>`)}</select></div>
        </div>
        <div class="row mt" style="flex-wrap:wrap;gap:16px">
          <div class="metric-checks">${METRICS.map(([k, l]) => html`<label class="check"><input type="checkbox" .checked=${st.metrics.includes(k)}
            @change=${(e: Event) => { const on = (e.target as HTMLInputElement).checked; set("metrics", on ? [...st.metrics, k] : st.metrics.filter((x) => x !== k)); }}>${l}</label>`)}</div>
          <label class="check"><input type="checkbox" .checked=${st.compare} @change=${(e: Event) => set("compare", (e.target as HTMLInputElement).checked)}>Compare to same period last year</label>
        </div>
      </section>

      <section class="glass card mt">
        ${sectionHead(autoTitle(d.query), d.result.rangeLabel ?? "", html`<div class="row">
          <button class="btn" ?disabled=${!d.result.rows.length} @click=${() => downloadCsv(autoTitle(d.query), d.result)}>${icons.download}<span>CSV</span></button>
        </div>`)}
        ${d.result.rows.length && d.result.dims.length === 1 ? html`<div style="margin-bottom:18px">${resultChart(d.result, chartKind)}</div>` : nothing}
        ${resultTable(d.result, { maxHeight: 560, onRow: d.result.dims.some((x) => x.key === "customer") ? (row) => app.go("customer", String(row.key.customer)) : undefined })}
        ${d.result.truncated ? html`<p class="muted small">Showing the top ${st.limit}. Totals include every row.</p>` : nothing}
        <div class="row mt" style="flex-wrap:wrap">
          <input class="input" style="flex:1;min-width:200px" placeholder=${autoTitle(d.query)} .value=${pinTitle} @input=${(e: Event) => { pinTitle = (e.target as HTMLInputElement).value; }}>
          <button class="btn primary" ?disabled=${!d.result.rows.length} @click=${pin}>${icons.pin}<span>Pin to My Board</span></button>
        </div>
        ${pinMsg ? html`<p class=${pinMsg.ok ? "small" : "err-text"} style="margin:10px 0 0">${pinMsg.text} ${pinMsg.ok ? html`<a class="link" href=${app.href("board")}>Open board</a>` : nothing}</p>` : nothing}
      </section>`;
  },
};

export function autoTitle(q: WidgetQuery): string {
  const m = q.metrics.map((k) => METRICS.find(([x]) => x === k)?.[1] ?? k).join(", ");
  const d = q.dims.map((k) => DIMS.find(([x]) => x === k)?.[1]?.toLowerCase() ?? k).join(" & ");
  return d ? `${m} by ${d}` : m;
}

